import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/**
 * Interactive checklist attached to an OPEN time entry. Persists each
 * toggle back to the server via `POST /api/time/entries/{id}/checklists/{cid}/check`.
 * Reverts optimistically on failure.
 */
export function ChecklistPanel({ entry, completion }) {
  const [checked, setChecked] = useState(() => new Set(completion.checked_item_ids || []));
  const [busy, setBusy] = useState(false);

  const items = (completion.snapshot_items || []).slice();
  const requiredTotal = items.filter((i) => i.required).length;
  const requiredChecked = items.filter((i) => i.required && checked.has(i.item_id)).length;

  const toggle = async (item_id, next) => {
    const prev = new Set(checked);
    if (next) prev.add(item_id); else prev.delete(item_id);
    setChecked(new Set(prev));
    setBusy(true);
    try {
      await api.post(
        `/time/entries/${entry.id}/checklists/${completion.checklist_id}/check`,
        { item_id, checked: next },
      );
    } catch (e) {
      toast.error(formatApiError(e));
      const rev = new Set(checked);
      setChecked(rev);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-lg border bg-muted/30 p-3"
      data-testid={`checklist-block-${completion.checklist_id}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="font-medium text-sm">{completion.checklist_name || "Checklist"}</div>
        {requiredTotal > 0 && (
          <Badge
            className={`rounded-full text-[10px] ${
              requiredChecked === requiredTotal
                ? "bg-success/15 text-success"
                : "bg-amber-500/15 text-amber-700"
            }`}
          >
            {requiredChecked}/{requiredTotal} required
          </Badge>
        )}
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <label
            key={it.item_id}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid={`checklist-item-${it.item_id}`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input text-secondary"
              disabled={busy}
              checked={checked.has(it.item_id)}
              onChange={(e) => toggle(it.item_id, e.target.checked)}
            />
            <span className={checked.has(it.item_id) ? "line-through text-muted-foreground" : ""}>
              {it.label}
            </span>
            {it.required && !checked.has(it.item_id) && (
              <span className="text-xs uppercase text-amber-700">required</span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Pre-shift checklist rendered inside the ClockInModal. Local state
 * only — the modal collects checked item IDs and sends them all with
 * the clock-in payload as `checklist_checks`.
 */
export function PreShiftChecklistPanel({ checklist, checkedIds, onToggle }) {
  const checkedSet = new Set(checkedIds || []);
  const requiredTotal = checklist.items.filter((i) => i.required).length;
  const requiredChecked = checklist.items.filter(
    (i) => i.required && checkedSet.has(i.item_id),
  ).length;

  return (
    <div
      className="rounded-lg border bg-muted/30 p-3"
      data-testid={`clock-in-checklist-${checklist.id}`}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium">{checklist.name}</div>
          {checklist.description && (
            <div className="text-xs text-muted-foreground">{checklist.description}</div>
          )}
        </div>
        {requiredTotal > 0 && (
          <Badge
            className={`shrink-0 rounded-full text-[10px] ${
              requiredChecked === requiredTotal
                ? "bg-success/15 text-success"
                : "bg-amber-500/15 text-amber-700"
            }`}
          >
            {requiredChecked}/{requiredTotal} required
          </Badge>
        )}
      </div>
      <div className="space-y-1.5">
        {checklist.items.map((it) => (
          <label
            key={it.item_id}
            className="flex items-center gap-2 text-sm cursor-pointer"
            data-testid={`clock-in-checklist-item-${it.item_id}`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input text-secondary"
              checked={checkedSet.has(it.item_id)}
              onChange={(e) => onToggle(checklist.id, it.item_id, e.target.checked)}
            />
            <span className={checkedSet.has(it.item_id) ? "line-through text-muted-foreground" : ""}>
              {it.label}
            </span>
            {it.required && !checkedSet.has(it.item_id) && (
              <span className="text-[10px] uppercase tracking-wider text-amber-700">required</span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}
